import { useState } from 'react';
import { useTranslations } from 'use-intl';
import {
  Body,
  Button,
  Card,
  Heading,
  I,
  Icon,
  Row,
  Stack,
} from '@leanin/ui';
import { useRestoreGroup } from '../../lib/mutations/groups';
import { useErrorMessage } from '../../lib/error-message';

type GroupKind = 'circle' | 'network' | 'space';

type Props = {
  readonly groupId: string;
  readonly kind: GroupKind;
  readonly viewerIsLeader: boolean;
};

export function GroupArchivedCallout({ groupId, kind, viewerIsLeader }: Props) {
  const t = useTranslations('Group');
  const describeError = useErrorMessage();
  const restoreMutation = useRestoreGroup();
  const [error, setError] = useState<string | null>(null);

  const handleRestore = () => {
    setError(null);
    restoreMutation.mutate(groupId, {
      onError: (err) =>
        setError(describeError(err, t('archived.restoreError', { kind }))),
    });
  };

  return (
    <Card pad="default" tone="neutral">
      <Stack gap="md">
        <Row gap="md" align="center" justify="space-between" wrap>
          <Row gap="md" align="center" style={{ minWidth: 0 }}>
            <Icon size={20} color="var(--mute)">{I.lock}</Icon>
            <Stack gap="none" style={{ minWidth: 0 }}>
              <Heading level={3}>{t('archived.heading', { kind })}</Heading>
              <Body size="sm" color="mute">
                {t('archived.body')}
              </Body>
            </Stack>
          </Row>
          {viewerIsLeader ? (
            <Button
              variant="primary"
              size="md"
              icon={I.spark}
              onClick={handleRestore}
              loading={restoreMutation.isPending}
            >
              {t('archived.restore', { kind })}
            </Button>
          ) : null}
        </Row>
        {error !== null ? (
          <Body size="sm" color="destructive">{error}</Body>
        ) : null}
      </Stack>
    </Card>
  );
}
